import { invitationAcceptanceMode as invitationAcceptanceModeFromStatus } from "@/lib/auth-callback";

export type InvitationLookup =
  | {
      ok: true;
      email: string;
      org_name: string | null;
      role: string;
      account_exists?: boolean;
      email_confirmed?: boolean;
      account_status?: "none" | "unconfirmed" | "confirmed";
      invitation_kind?: string;
    }
  | { ok: false; code: "MISSING" | "MALFORMED" | "INVALID" | "EXPIRED" | "USED" | "WRONG_EMAIL" | "FORBIDDEN_ROLE" | "ALREADY_REGISTERED" };

export const INVITE_TOKEN_STORAGE_KEY = "co_invite_token";

export function invitationTokenLooksWellFormed(token: string | null | undefined): boolean {
  const t = (token ?? "").trim();
  return t.length === 64 && /^[0-9a-fA-F]+$/.test(t);
}

export function invitationTokenFromSearch(raw: string | null | undefined): string {
  return (raw ?? "").trim();
}

/** Canonical invitee URL path. Plaintext token is a query param, never stored in invitation rows. */
export function invitationRegisterPath(token: string): string {
  return `/register?invite=${encodeURIComponent(token.trim())}`;
}

/** Sign-in URL that carries the same single-use token through to initialize_account. */
export function invitationLoginPath(token: string): string {
  return `/login?invite=${encodeURIComponent(token.trim())}`;
}

export function persistInvitationToken(token: string | null | undefined, storage: Pick<Storage, "setItem"> = localStorage): string | null {
  const t = invitationTokenFromSearch(token);
  if (!invitationTokenLooksWellFormed(t)) return null;
  storage.setItem(INVITE_TOKEN_STORAGE_KEY, t);
  return t;
}

export function readPersistedInvitationToken(storage: Pick<Storage, "getItem"> = localStorage): string | null {
  const t = storage.getItem(INVITE_TOKEN_STORAGE_KEY);
  return invitationTokenLooksWellFormed(t) ? (t ?? "").trim() : null;
}

export function clearPersistedInvitationToken(storage: Pick<Storage, "removeItem"> = localStorage): void {
  storage.removeItem(INVITE_TOKEN_STORAGE_KEY);
}

/**
 * After a valid invitation lookup only.
 * Unconfirmed Auth rows must confirm email; they cannot sign in yet.
 */
export function invitationAcceptanceMode(
  lookup: {
    ok?: boolean;
    account_exists?: boolean;
    account_status?: string;
    email_confirmed?: boolean;
  } | null | undefined,
): "register" | "sign_in" | "confirm_email" | "closed" {
  return invitationAcceptanceModeFromStatus(lookup);
}

export function registerRequiresInvitationForm(token: string | null | undefined): boolean {
  return invitationTokenLooksWellFormed(token);
}

export function invitationErrorMessage(code: string | undefined): string {
  switch (code) {
    case "MISSING":
      return "A Tradivo invitation is required to create an account.";
    case "MALFORMED":
      return "This invitation link is not valid.";
    case "EXPIRED":
      return "This invitation has expired. Ask an administrator to send a new one.";
    case "USED":
      return "This invitation has already been used.";
    case "WRONG_EMAIL":
      return "This invitation was issued to a different email address.";
    case "FORBIDDEN_ROLE":
      return "This invitation cannot be accepted.";
    case "ALREADY_REGISTERED":
      return "Sign in to accept this invitation.";
    default:
      return "This invitation is not valid.";
  }
}
